/**
 * @quotech/auth - Shared Domain Cookie Authentication
 *
 * PATH: packages/auth/src/cookieAuth.ts
 * PURPOSE: Unified session management across all Quotech ERP hubs
 *
 * Enables seamless hub navigation by setting auth cookies at the .quotech.ai domain level.
 * All subdomains (dashboard, qa, crm, etc.) can read this cookie, eliminating SSO redirects.
 *
 * COOKIES MANAGED:
 * - quotech_auth: JWT access token (7 days)
 * - quotech_refresh: JWT refresh token (30 days)
 * - quotech_org: Organisation context { id, name }
 * - quotech_integrations: Integration status (Xero, Simpro, OneDrive, Email)
 * - quotech_session: Session metadata (login time, last activity, device)
 */

// Re-export everything from sub-modules
export { MASTER_LOGIN_URL, type IntegrationStatus, type SessionMetadata, type LogoutReason } from './cookie-auth/constants'
export {
  setAuthCookie, getAuthCookie, clearAuthCookie,
  setRefreshCookie, getRefreshCookie, clearRefreshCookie,
  setOrgCookie, getOrgCookie, clearOrgCookie,
  setRememberMeCookie, getRememberMeCookie, clearRememberMeCookie,
} from './cookie-auth/coreCookies'
export {
  setIntegrationsCookie, getIntegrationsCookie, clearIntegrationsCookie, isXeroExpired,
  setSessionCookie, getSessionCookie, updateSessionActivity, clearSessionCookie,
  getInactivityDuration,
} from './cookie-auth/sessionCookies'
export {
  isValidJWT, getJWTPayload, isTokenExpired, getTokenExpiry,
  isCookieAuthValid, needsTokenRefresh,
} from './cookie-auth/tokenValidation'

// Imports needed for functions defined in this file
import { getAuthCookie, clearAuthCookie, clearRefreshCookie, clearOrgCookie, clearRememberMeCookie } from './cookie-auth/coreCookies'
import { clearIntegrationsCookie, clearSessionCookie, setSessionCookie } from './cookie-auth/sessionCookies'
import { setAuthCookie, setRefreshCookie, setOrgCookie } from './cookie-auth/coreCookies'
import { MASTER_LOGIN_URL, type LogoutReason } from './cookie-auth/constants'

/** Clear all auth cookies (full logout) */
export function clearAllAuthCookies(): void {
  clearAuthCookie()
  clearRefreshCookie()
  clearOrgCookie()
  clearIntegrationsCookie()
  clearSessionCookie()
  clearRememberMeCookie()
}

/** Set all auth data at once (convenience method for login) */
export function setAuthData(
  accessToken: string,
  refreshToken?: string,
  orgId?: string,
  orgName?: string,
  hubOrigin?: string,
): void {
  setAuthCookie(accessToken)
  if (refreshToken) setRefreshCookie(refreshToken)
  if (orgId) setOrgCookie(orgId, orgName)
  setSessionCookie(hubOrigin || 'dashboard')
}

/** Check if user has valid auth cookie */
export function hasAuthCookie(): boolean {
  return getAuthCookie() !== null
}

/** Redirect to master login page */
export function redirectToMasterLogin(reason: LogoutReason, returnTo?: string): void {
  if (typeof window === 'undefined') return

  // Don't redirect if already on master login
  const currentHost = window.location.hostname
  const masterHost = new URL(MASTER_LOGIN_URL).hostname
  if (currentHost === masterHost) {
    console.log('[@quotech/auth] Already on master login page, skipping redirect')
    window.dispatchEvent(new CustomEvent('quotech:session_expired', { detail: { reason } }))
    return
  }

  const currentUrl = returnTo || window.location.href
  const params = new URLSearchParams({ returnTo: currentUrl, reason })
  window.location.href = `${MASTER_LOGIN_URL}?${params.toString()}`
}

/** Migrate existing localStorage auth to cookies */
export function migrateLocalStorageToCookie(): void {
  if (typeof window === 'undefined') return
  if (hasAuthCookie()) return

  const accessToken = localStorage.getItem('IdToken') || localStorage.getItem('accessTokennew') || localStorage.getItem('accessToken')
  const refreshToken = localStorage.getItem('refreshToken')
  const orgId = localStorage.getItem('CurrentTenantId')
  const orgName = localStorage.getItem('CurrentTenantName')

  if (accessToken) {
    console.log('[@quotech/auth] Migrating auth from localStorage to shared cookie')
    setAuthData(accessToken, refreshToken || undefined, orgId || undefined, orgName || undefined)
  }
}

// Legacy name for backwards compatibility
export const migrateLocalStorageToCooki = migrateLocalStorageToCookie
